import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Employee } from '../up-board/up-board.component';
import { NormalizedEmployee, ChannelType } from 'src/app/utils/employee.model';

/**
 * UpBoardEmployeeComponent - Employee Card Display with Visual Status Indicators
 * 
 * Displays an employee card with:
 * - Employee photo and name
 * - State-based visual indicators (icons, colors, size, opacity)
 * - Real-time updates when state changes
 * 
 * **Data Flow:**
 * - Receives both legacy Employee format (for backward compatibility) 
 * - AND normalized multi-channel employee data via optional inputs
 * - Uses showDelivery flag to conditionally display delivery states
 * 
 * **Visual Indicators (Story 3.2):**
 * - Icon: Font Awesome icon based on channel state
 * - Color: WCAG 2.1 AA compliant color based on state
 * - Size & Opacity: 100% for available, 80% for unavailable (with reduced opacity 60%)
 * 
 * @see Story 3.2 - Implement Visual Status Indicators in Floor Ups Column
 */
@Component({
  selector: 'app-up-board-employee',
  templateUrl: './up-board-employee.component.html',
  styleUrls: ['./up-board-employee.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpBoardEmployeeComponent implements OnInit {

  @Input() editMode: boolean = false;
  @Input() up: boolean = false;
  @Input() onDeck: boolean = false;
  @Input() emp: Employee | null = null;
  @Input() index: number | null = null;
  
  // Story 3.2: New inputs for visual status indicators
  @Input() normalized?: NormalizedEmployee;          // Normalized multi-channel employee data
  @Input() channel: ChannelType = 'instore';         // Current channel context
  @Input() showDelivery: boolean = true;             // Whether to display delivery states
  
  // Story 3.3: Cross-channel unavailability indicator
  @Input() unavailable?: boolean;                    // Passed from parent to indicate unavailability
  
  // Cross-channel UP/OnDeck status for showing appropriate icons
  @Input() isUpInInstore?: boolean;                  // True if employee is UP in Floor Ups
  @Input() isOnDeckInInstore?: boolean;              // True if employee is On Deck in Floor Ups
  
  // Inactive employee mode - shows activate button instead of state icon
  @Input() isInactive: boolean = false;              // True if employee is in inactive section
  @Output() activate = new EventEmitter<void>();     // Emits when activate button is clicked

  constructor() {}

  ngOnInit(): void {
  }

  /**
   * Get Font Awesome icon name based on employee state in current channel.
   * Respects showDelivery flag to conditionally show/hide delivery states.
   * Returns null/empty string for available state (no icon).
   * 
   * Priority for icons:
   * 1. No icon for UP or OnDeck in current channel (they're active/ready)
   * 2. Arrow-up if unavailable because UP in Floor Ups (instore)
   * 3. Hourglass if unavailable because On Deck in Floor Ups (instore)
   * 4. State-based icon for busy states (with_guest, with_apt, etc.)
   * 
   * @returns Font Awesome icon name (without 'fa-' prefix), or null for available state
   */
  getStateIcon(): string | null {
    if (!this.normalized) {
      return 'question';
    }

    // No icon for UP or OnDeck in current channel - they're active/ready
    if (this.up || this.onDeck) {
      return null;
    }

    // Check global state first
    if (this.normalized.globalState === 'break') {
      return 'coffee'; // Coffee cup - break
    }

    // Cross-channel unavailability: UP or OnDeck in Floor Ups (instore)
    // Only applies to phone/online channels
    if (this.unavailable && this.channel !== 'instore') {
      // Arrow-up if unavailable because UP in Floor Ups
      if (this.isUpInInstore) {
        return 'arrow-up';
      }
      
      // Hourglass if unavailable because On Deck in Floor Ups
      if (this.isOnDeckInInstore) {
        return 'hourglass-half';
      }
      
      // Check if they're busy in the instore channel and show that icon
      const currentChannelState = this.normalized.channels[this.channel]?.state || 'inactive';
      const instoreState = this.normalized.channels.instore?.state;
      
      // Only override if current channel shows available but they're busy in instore
      if (currentChannelState === 'available' && instoreState) {
        const busyStates = ['with_guest', 'with_apt', 'with_guest_delivery', 'with_apt_delivery'];
        if (busyStates.includes(instoreState)) {
          // Show the instore busy state icon
          if (!this.showDelivery && (instoreState === 'with_apt_delivery' || instoreState === 'with_guest_delivery')) {
            return 'calendar-check-o';
          }
          
          switch (instoreState) {
            case 'with_apt':
              return 'calendar-check-o'; // Calendar with checkmark - appointment
            case 'with_apt_delivery':
              return 'truck'; // Truck - delivery
            case 'with_guest':
              return 'user-circle'; // Person icon - walk-in customer
            case 'with_guest_delivery':
              return 'truck'; // Truck - delivery
          }
        }
      }
    }

    const state = this.normalized.channels[this.channel]?.state || 'inactive';

    // If delivery states hidden and employee in delivery state, map to appointment
    if (!this.showDelivery && (state === 'with_apt_delivery' || state === 'with_guest_delivery')) {
      return 'calendar-check-o';
    }

    switch (state) {
      case 'available':
        return null; // No icon for available state
      case 'with_apt':
         return 'calendar-check-o'; // Calendar with checkmark - appointment
       case 'with_apt_delivery':
         return 'truck'; // Truck - delivery
       case 'with_guest':
         return 'user-circle'; // Person icon - walk-in customer
       case 'with_guest_delivery':
         return 'truck'; // Truck - delivery
      // Note: 'inactive' is now a global state only (see GlobalState type)
      default:
        return 'question'; // Unknown state
    }
  }

  /**
   * Get standardized dark color for all status icons.
   * Uses the same dark color (#1a1a1a) for all states to provide visual consistency.
   * 
   * @returns Hex color code ('#1a1a1a' - very dark gray/black)
   */
  getStateColor(): string {
    // Standardized dark color for all icons (same as break state)
    return '#1a1a1a';
  }

  /**
   * Determine if employee is unavailable (not in available state).
   * Unavailable employees (break, inactive, with_apt) get reduced size (80%) and opacity (60%). Delivery states remain full size with only icon change.
   * 
   * @returns true if employee should appear grayed out/reduced
   */
  isUnavailable(): boolean {
    if (!this.normalized) {
      return false;
    }

    const state = this.normalized.channels[this.channel]?.state || 'inactive';
    const globalState = this.normalized.globalState;

    // On break, inactive, or any non-available state → unavailable
    return globalState === 'break' ||
           (state !== 'available' && state !== 'with_guest' && state !== 'with_guest_delivery' && state !== 'with_apt_delivery');
  }

  /**
   * Determines if employee should show full unavailability CSS class (60% opacity + 80% size).
   * Story 3.3: Cross-channel unavailability indicators.
   * 
   * @returns true if .unavailable class should be applied
   */
  shouldApplyUnavailableClass(): boolean {
    if (!this.unavailable) {
      return false;
    }

    // Must have normalized data to determine state
    if (!this.normalized) {
      return false;
    }

    // In Floor Ups, only apply full unavailability (full opacity + size reduction) for break state
    // Other busy states get size-only treatment
    if (this.channel === 'instore') {
      if (this.normalized.globalState === 'break') {
        return true; // Break is most unavailable - full opacity reduction
      }
      // Other states in instore get size-only treatment (checked by shouldApplyUnavailableSizeOnlyClass)
      return false;
    }

    // In phone/online columns, all unavailable employees get full treatment
    return true;
  }

  /**
   * Determines if employee should show size-only unavailability CSS class (80% size, 100% opacity).
   * Story 3.3: Cross-channel unavailability indicators.
   * Only applies to Floor Ups column for busy states.
   * 
   * @returns true if .unavailable-size-only class should be applied
   */
  shouldApplyUnavailableSizeOnlyClass(): boolean {
    if (!this.unavailable) {
      return false;
    }

    // Only applies to Floor Ups (instore) column
    if (this.channel !== 'instore') {
      return false;
    }

    if (!this.normalized) {
      return false;
    }

    // Break state uses full unavailability (opacity + size), not size-only
    if (this.normalized.globalState === 'break') {
      return false;
    }

    // Size-only applies to busy states (with_apt, with_apt_delivery) in Floor Ups
    return true;
  }

}
